import { DocumentAccessData } from "@chinese-vendor/schemas";
import { apiClient } from "./client";
import endpoint from "@/constants/endpoint";

// ── Enums ─────────────────────────────────────────────────────────────────────

export type ReviewDecision = "approve" | "reject" | "request_changes";
export type ApplicationStatus = "pending_review" | "approved" | "rejected";
export type FieldReviewStatus = "approved" | "rejected";

// ── Checklist ─────────────────────────────────────────────────────────────────

export type ApprovalChecklistItem =
  | "legal_representative_verified"
  | "business_registration_verified"
  | "compliance_certificates_verified"
  | "financial_settlement_verified"
  | "brand_assets_verified";

export const APPROVAL_CHECKLIST_ITEMS: ApprovalChecklistItem[] = [
  "legal_representative_verified",
  "business_registration_verified",
  "compliance_certificates_verified",
  "financial_settlement_verified",
  "brand_assets_verified",
];

// ── Shared ────────────────────────────────────────────────────────────────────

export interface RequestedChangeItem {
  field_key: string;
  reason: string;
}

// ── List types (matches backend ApplicationListItem / ApplicationListResponse) ─

export interface ApplicationSummary {
  id: string;
  company_name: string | null;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  primary_industry: string | null;
  submitted_at: string | null;
  status: ApplicationStatus;
  vendor_email: string | null;
  last_activity: string | null;
  decision_at: string | null;
}

export interface ApplicationsListResponse {
  items: ApplicationSummary[];
  total: number;
  offset: number;
  limit: number;
  counts: {
    pending_review?: number;
    approved?: number;
    rejected?: number;
  };
}

// ── Stats ─────────────────────────────────────────────────────────────────────

export interface ApplicationStats {
  new_applications: number;
  approved_applications: number;
  rejected_applications: number;
}

// ── Detail types ──────────────────────────────────────────────────────────────

export interface LegalRepresentative {
  fullNameChinese: string;
  fullNameEnglish: string;
  nationality: string;
  role: string;
  department: string;
  phone: string;
  email: string;
  idType: string;
  idDocumentUrl?: string;
  idDocumentSizeLabel?: string;
  wechatQrUrl?: string;
  wechatQrSizeLabel?: string;
}

export interface BusinessInformation {
  companyNameChinese: string;
  companyNameEnglish: string;
  companyNameArabic?: string;
  registeredAddress: string;
  city: string;
  postalCode: string;
  ussc: string;
  businessLicenseUrl?: string;
  businessLicenseSizeLabel?: string;
}

export interface ComplianceCertificate {
  fileName: string;
  fileSizeLabel: string;
  fileUrl: string;
}

export interface ProductCompliance {
  operatingIndustries: string[];
  operatingCategories: string[];
  isoCertificate?: ComplianceCertificate;
  ceMarking?: ComplianceCertificate;
  qualityTestReports?: ComplianceCertificate[];
  ecoCertifications?: ComplianceCertificate[];
}

export interface BankSettlement {
  accountHolderName: string;
  accountNumber: string;
  iban?: string;
  swiftCode: string;
  bankAddress: string;
  settlementCurrency: string;
}

export interface AlipaySettlement {
  merchantId: string;
  businessName: string;
  email: string;
  settlementCurrency: string;
  qrCodeUrl?: string;
}

export interface WechatPaySettlement {
  merchantId: string;
  businessName: string;
  email: string;
  settlementAccountType: string;
  settlementCurrency: string;
  qrCodeUrl?: string;
}

export interface FinancialSettlement {
  bank?: BankSettlement;
  alipay?: AlipaySettlement;
  wechatPay?: WechatPaySettlement;
}

export interface BrandingLocalization {
  shortNameEnglish: string;
  shortNameArabic?: string;
  shortNameChinese?: string;
  descriptionEnglish: string;
  descriptionArabic?: string;
  logoUrl?: string;
  logoSizeLabel?: string;
}

export type ActivityLogEventType =
  | "approved"
  | "rejected"
  | "submitted"
  | "resubmitted"
  | "requested_updates";

export interface ActivityLogEntry {
  id: string;
  type: ActivityLogEventType;
  actorName: string;
  timestamp: string;
}

export interface FieldRejectionInfo {
  reason?: string;
  note?: string;
  rejectedAt?: string;
  rejectedBy?: { name: string; role: string };
}

export interface FieldEditInfo {
  currentValue?: string;
  previousValue?: string;
  editedAt?: string;
  editedBy?: { name: string; role: string };
}

export interface ApplicationDetail {
  id: string;
  companyName: string;
  status: ApplicationStatus;
  submittedAt: string;
  rejectedBy?: { name: string; role: string };
  rejectedAt?: string;
  rejectionReason?: string;
  legalRepresentative?: LegalRepresentative;
  businessInformation?: BusinessInformation;
  productCompliance?: ProductCompliance;
  financialSettlement?: FinancialSettlement;
  brandingLocalization?: BrandingLocalization;
  activityLog?: ActivityLogEntry[];
  fieldRejections?: Record<string, FieldRejectionInfo>;
  fieldEdits?: Record<string, FieldEditInfo>;
}

// ── Decision request ──────────────────────────────────────────────────────────

export type ReviewDecisionRequest =
  | {
      action: "approve";
      checklist: ApprovalChecklistItem[];
      reason?: never;
      requested_changes?: never;
    }
  | { action: "reject"; reason: string; checklist?: never; requested_changes?: never }
  | {
      action: "request_changes";
      requested_changes: RequestedChangeItem[];
      reason?: never;
      checklist?: never;
    };

// ── Field review ──────────────────────────────────────────────────────────────

export interface FieldReview {
  fieldName: string;
  decision: FieldReviewStatus;
  note?: string;
}

export interface FieldReviewResponse {
  id: string;
  field_key: string;
  status: FieldReviewStatus;
  reason: string | null;
  internal_note: string | null;
  reviewer_name: string;
}

export interface FieldEditResponse {
  id: string;
  field_key: string;
  old_value: string | null;
  new_value: string;
  editor_name: string;
  editor_role: string | null;
}

// ── Decision response ─────────────────────────────────────────────────────────

export interface ReviewDecisionResponse {
  id: string;
  vendor_profile_id: string;
  action: string;
  new_status: string;
  reason: string | null;
  requested_changes: RequestedChangeItem[] | null;
}

export interface UndoDecisionResponse {
  id: string;
  vendor_profile_id: string;
  previous_status: string;
  new_status: string;
}

// ── Field reviews list ────────────────────────────────────────────────────────

export interface FieldReviewItem {
  id: string;
  field_key: string;
  status: FieldReviewStatus;
  reason: string | null;
  internal_note: string | null;
  reviewer_name: string;
  created_at: string;
}

export interface StaffEditItem {
  id: string;
  field_key: string;
  old_value: string | null;
  new_value: string;
  editor_name: string;
  editor_role: string | null;
  created_at: string;
}

export interface FieldReviewsListResponse {
  reviews: FieldReviewItem[];
  edits: StaffEditItem[];
  progress: {
    sections_visited: number;
    total_sections: number;
    fields_audited: number;
    total_reviewable_fields: number;
  };
}

// ── Field key mapping (UI camelCase ↔ backend stepN.snake_case) ──────────────

/** Maps UI field identifiers to their i18n label keys for display in review modals. */
const UI_FIELD_KEY_TO_LABEL_KEY: Record<string, string> = {
  // Step 2 — Business Information
  companyNameEnglish: "review.application.businessInformation.fields.companyNameEnglish",
  companyNameArabic: "review.application.businessInformation.fields.companyNameArabic",
  ussc: "review.application.businessInformation.fields.ussc",
  businessLicense: "review.application.businessInformation.fields.businessLicense",
  // Step 5 — Branding & Localization
  shortNameEnglish: "review.application.brandingLocalization.fields.shortNameEnglish",
  shortNameChinese: "review.application.brandingLocalization.fields.shortNameChinese",
  shortNameArabic: "review.application.brandingLocalization.fields.shortNameArabic",
  descriptionEnglish: "review.application.brandingLocalization.fields.descriptionEnglish",
  descriptionArabic: "review.application.brandingLocalization.fields.descriptionArabic",
};

/** Returns the i18n label key for a UI field key, or the key itself if not found. */
export function fieldLabelKey(uiKey: string): string {
  return UI_FIELD_KEY_TO_LABEL_KEY[uiKey] ?? uiKey;
}

/** Maps UI field identifiers used by section components to backend field_key format. */
const UI_FIELD_KEY_TO_BACKEND: Record<string, string> = {
  // Step 1 — Legal Representative
  id_type: "step1.legal_representative_id_document",
  // Step 2 — Business Information
  companyNameEnglish: "step2.company_name_english",
  companyNameArabic: "step2.company_name_arabic",
  ussc: "step2.unified_social_credit_code",
  businessLicense: "step2.business_license_document",
  // Step 3 — Product Compliance
  operatingIndustries: "step3.industry",
  operatingCategories: "step3.product_subcategories",
  isoCertificate: "step3.iso_certification_document",
  ceMarking: "step3.ce_marking_document",
  qualityTestReports: "step3.quality_test_reports",
  ecoCertifications: "step3.eco_certification_document",
  // Step 4 — Financial Settlement
  alipayMerchantId: "step4.alipay_merchant_id",
  wechatMerchantId: "step4.wechatpay_merchant_id",
  // Step 5 — Branding & Localization
  shortNameEnglish: "step5.business_short_name_english",
  shortNameChinese: "step5.business_short_name_chinese",
  shortNameArabic: "step5.business_short_name_arabic",
  descriptionEnglish: "step5.business_description_english",
  descriptionArabic: "step5.business_description_arabic",
  businessLogo: "step5.business_logo",
};

const BACKEND_FIELD_KEY_TO_UI: Record<string, string> = Object.fromEntries(
  Object.entries(UI_FIELD_KEY_TO_BACKEND).map(([ui, be]) => [be, ui])
);

/** Translate a UI field key (e.g. "companyNameEnglish") to the backend format (e.g. "step2.company_name_english").
 *  Handles indexed variants like "qualityTestReports_0" → "step3.quality_test_reports_0". */
export function toBackendFieldKey(uiKey: string): string {
  const indexMatch = uiKey.match(/^(.+)_(\d+)$/);
  if (indexMatch) {
    const base = UI_FIELD_KEY_TO_BACKEND[indexMatch[1]];
    return base ? `${base}_${indexMatch[2]}` : uiKey;
  }
  return UI_FIELD_KEY_TO_BACKEND[uiKey] ?? uiKey;
}

/** Translate a backend field_key (e.g. "step2.company_name_english") to the UI key (e.g. "companyNameEnglish").
 *  Handles indexed variants like "step3.quality_test_reports_0" → "qualityTestReports_0". */
export function fromBackendFieldKey(backendKey: string): string {
  if (BACKEND_FIELD_KEY_TO_UI[backendKey]) return BACKEND_FIELD_KEY_TO_UI[backendKey];
  const indexMatch = backendKey.match(/^(.+)_(\d+)$/);
  if (indexMatch) {
    const uiBase = BACKEND_FIELD_KEY_TO_UI[indexMatch[1]];
    return uiBase ? `${uiBase}_${indexMatch[2]}` : backendKey;
  }
  return backendKey;
}

// ── API ───────────────────────────────────────────────────────────────────────

export const reviewApi = {
  /** Get application stats (24-hour window) */
  async getStats(): Promise<ApplicationStats> {
    const res = await apiClient.get<ApplicationStats>(endpoint.review.stats);
    return res.data;
  },

  /** List applications with pagination and optional filters */
  async list(params?: {
    status?: "pending_review" | "approved" | "rejected";
    search?: string;
    offset?: number;
    limit?: number;
  }): Promise<ApplicationsListResponse> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.search) qs.set("search", params.search);
    if (params?.offset != null) qs.set("offset", String(params.offset));
    if (params?.limit != null) qs.set("limit", String(params.limit));
    const query = qs.toString();
    const path = query ? `${endpoint.review.applications}?${query}` : endpoint.review.applications;
    const res = await apiClient.get<ApplicationsListResponse>(path);
    return res.data;
  },

  /** Get full application details */
  async getApplication(id: string): Promise<ApplicationDetail> {
    const res = await apiClient.get<ApplicationDetail>(endpoint.review.application(id));
    return res.data;
  },

  /** Submit an overall review decision (approve / reject / request_changes) */
  async submitDecision(id: string, body: ReviewDecisionRequest): Promise<ReviewDecisionResponse> {
    const res = await apiClient.post<ReviewDecisionResponse>(
      endpoint.review.applicationDecision(id),
      body
    );
    return res.data;
  },

  /** Revert an approved or rejected application back to pending_review */
  async undoDecision(id: string): Promise<UndoDecisionResponse> {
    const res = await apiClient.post<UndoDecisionResponse>(
      endpoint.review.applicationUndoDecision(id),
      {}
    );
    return res.data;
  },

  /** Submit a field-level review (approve or reject a single field) */
  async submitFieldReview(
    id: string,
    fieldKey: string,
    status: FieldReviewStatus,
    reason?: string,
    internalNote?: string
  ): Promise<FieldReviewResponse> {
    const res = await apiClient.put<FieldReviewResponse>(
      endpoint.review.applicationFieldReview(id),
      {
        field_key: fieldKey,
        status,
        reason: reason ?? null,
        internal_note: internalNote ?? null,
      }
    );
    return res.data;
  },

  /** Remove a field-level review */
  async deleteFieldReview(id: string, fieldKey: string): Promise<void> {
    await apiClient.delete(endpoint.review.applicationFieldReview(id), {
      field_key: fieldKey,
    });
  },

  /** Submit an admin edit to a single field value */
  async submitFieldEdit(id: string, fieldKey: string, value: string): Promise<FieldEditResponse> {
    const res = await apiClient.put<FieldEditResponse>(endpoint.review.applicationFieldEdit(id), {
      field_key: fieldKey,
      value,
    });
    return res.data;
  },

  /** Remove an admin field edit (restore original value) */
  async deleteFieldEdit(id: string, fieldKey: string): Promise<void> {
    await apiClient.delete(endpoint.review.applicationFieldEdit(id), {
      field_key: fieldKey,
    });
  },

  /** Get all field reviews and edits for an application */
  async getFieldReviews(id: string): Promise<FieldReviewsListResponse> {
    const res = await apiClient.get<FieldReviewsListResponse>(
      endpoint.review.applicationFieldReviews(id)
    );
    return res.data;
  },

  /**
   * Get a time-limited presigned access URL for a previously uploaded document.
   * POST /api/v1/admin/applications/{application_id}/documents/access
   */
  async getDocumentAccessUrl(id: string, key: string): Promise<DocumentAccessData> {
    const res = await apiClient.post<DocumentAccessData>(endpoint.review.documentAccess(id), {
      key,
      mode: "view",
    });
    return res.data;
  },
};
